/**
 * Records a blocked-because-stale apply attempt: the analytics event plus the
 * marker on the owning assistant message.
 *
 * Extracted from AutoRedlinePreview.handleApplyAll, which had grown to carry
 * seven concerns in one function.
 */

import { useCallback } from "react";
import { AutoRedlineOutput } from "../../../interfaces/autoRedlineTypes";
import { StaleApplyAttempt } from "../../../interfaces/chatSession";
import { useAgent } from "../../../contexts/AgentContext";
import { useSession } from "../../../contexts/SessionContext";
import { track } from "../../../utils/analytics";

/**
 * @param messageId Owning assistant message. Absent (some test renders) means
 *   the analytics event still fires but there is nothing to annotate.
 */
export function useStaleApplyRecorder(messageId?: string) {
  const { currentAgent } = useAgent();
  const { getAgentSession, updateMessageForAgent } = useSession();

  return useCallback(
    (redline: AutoRedlineOutput) => {
      // The prod-visible signal for stale blocks — the in-document marker only
      // rides along in chat_history, which made the CS-481 incident nearly
      // untraceable. No section name: that is contract content, and document
      // content never goes to analytics.
      track("redline:apply_blocked_stale", {
        source: "preview",
        paragraph_id: redline.paragraphId !== undefined ? String(redline.paragraphId) : undefined,
      });

      if (!messageId || !currentAgent?.id) return;

      // Record onto the owning message so the BE sees the blocked-apply event on
      // the next request. Avoid duplicate entries for the same section+paragraph
      // if the user repeatedly clicks Apply.
      const session = getAgentSession(currentAgent.id);
      const owningMessage = session?.messages.find((m) => m.id === messageId);
      const existingAttempts = owningMessage?.staleApplyAttempts ?? [];
      const alreadyRecorded = existingAttempts.some(
        (a) => a.sectionName === redline.sectionName && a.paragraphId === redline.paragraphId
      );
      if (alreadyRecorded) return;

      const newAttempt: StaleApplyAttempt = {
        sectionName: redline.sectionName,
        paragraphId: redline.paragraphId,
      };
      updateMessageForAgent(currentAgent.id, messageId, {
        staleApplyAttempts: [...existingAttempts, newAttempt],
      });
    },
    [messageId, currentAgent, getAgentSession, updateMessageForAgent]
  );
}
